/**
 * Decoration method classification for product selection rules.
 * Print methods (DTG, DTF, etc.) cannot be combined with embroidery.
 */

export type DecorationCategory = 'print' | 'embroidery';

interface MethodWithCode {
  code?: string | null;
}

/** A raw `PositionDecorationMethod` row, as GET /products/:id returns it. */
interface DecorationMethodJoinRow {
  isDefault?: boolean;
  decorationMethod?: MethodWithCode | null;
}

/**
 * The decoration method of a position, in every shape it arrives in: the
 * list endpoint flattens `decorationMethods` to the one default method, the
 * detail endpoint answers the raw join rows, and the shared
 * `ProductPosition` type carries `decorationMethod`. Direct Sale loads its
 * product through the detail endpoint — reading only the flat shape made it
 * treat embroidery as print and put the garment shading over the thread.
 */
interface PositionWithDecorationMethod {
  isDefault?: boolean;
  decorationMethods?:
    | MethodWithCode
    | ReadonlyArray<DecorationMethodJoinRow>
    | null;
  decorationMethod?: MethodWithCode | null;
}

export interface ProductWithDecorationMethod {
  positions?: ReadonlyArray<PositionWithDecorationMethod>;
}

function decorationCodeOf(
  position: PositionWithDecorationMethod | undefined
): string | null | undefined {
  const methods = position?.decorationMethods;
  if (Array.isArray(methods)) {
    const rows = methods as ReadonlyArray<DecorationMethodJoinRow>;
    // Same rule as the list endpoint: only a flagged row is the default —
    // an unflagged first row must not become one by position.
    const row = rows.find((entry) => entry.isDefault);
    return row?.decorationMethod?.code ?? position?.decorationMethod?.code;
  }
  return (
    (methods as MethodWithCode | null | undefined)?.code ??
    position?.decorationMethod?.code
  );
}

/**
 * Category per catalog code. Explicit rather than implicit so a growing
 * catalog cannot silently reclassify: SEWING and DIGITAL are print methods
 * by product decision, EMBROIDERY is the only embroidery method today.
 */
const CATEGORY_BY_CODE: Record<string, DecorationCategory> = {
  DTG: 'print',
  DTF: 'print',
  EMBROIDERY: 'embroidery',
  SEWING: 'print',
  SUBLIMATION: 'print',
  DIGITAL: 'print',
  SCREENPRINT: 'print',
};

export function getDecorationCategoryFromCode(
  code: string | undefined | null
): DecorationCategory | null {
  if (!code) return null;
  // Unknown codes fall back to print — the conservative default, since
  // embroidery is the category that triggers the product-mix conflict.
  return CATEGORY_BY_CODE[code] ?? 'print';
}

export function getProductDecorationCategory(
  product: ProductWithDecorationMethod
): DecorationCategory | null {
  const positions = product.positions ?? [];
  if (positions.length === 0) return null;

  const defaultPosition =
    positions.find((position) => position.isDefault) ?? positions[0];
  const code = decorationCodeOf(defaultPosition);

  return getDecorationCategoryFromCode(code);
}

/**
 * Whether the product is decorated by embroidery. The mockup stack puts the
 * garment shading below the design for embroidery (thread must not be
 * darkened by folds), above it for every print method.
 */
export function isEmbroideryProduct(
  product: ProductWithDecorationMethod
): boolean {
  return getProductDecorationCategory(product) === 'embroidery';
}

export function hasDecorationMethodConflict(
  activeCategory: DecorationCategory,
  newCategory: DecorationCategory
): boolean {
  return activeCategory !== newCategory;
}

export function wouldConflictWithActiveProducts(
  products: Array<
    { id: string; isActive: boolean } & ProductWithDecorationMethod
  >,
  productIdToActivate: string
): boolean {
  const productToActivate = products.find(
    (product) => product.id === productIdToActivate
  );
  if (!productToActivate) return false;

  const newCategory = getProductDecorationCategory(productToActivate);
  if (!newCategory) return false;

  return products.some((product) => {
    if (product.id === productIdToActivate || !product.isActive) return false;

    const activeCategory = getProductDecorationCategory(product);
    if (!activeCategory) return false;

    return hasDecorationMethodConflict(activeCategory, newCategory);
  });
}

interface NamedMethod {
  code?: string | null;
  name?: string | null;
  description?: string | null;
  isActive?: boolean;
}

/** A position as the product card reads it: default method plus all offered ones. */
interface PositionWithNamedMethods {
  decorationMethods?: NamedMethod | null;
  availableDecorationMethods?: ReadonlyArray<NamedMethod> | null;
}

/**
 * The method name the product card shows. An embroidery run is stitched
 * whatever the catalogue default is, so it names the product's embroidery
 * method; every other case keeps the first position's default (print runs
 * use it, and a product without a design has no run yet).
 */
export function cardMethodLabel(
  positions: ReadonlyArray<PositionWithNamedMethods>,
  designDecoration: DecorationCategory | null | undefined
): string {
  if (designDecoration === 'embroidery') {
    for (const position of positions) {
      const method = position.availableDecorationMethods?.find(
        (m) =>
          m.isActive !== false &&
          getDecorationCategoryFromCode(m.code) === 'embroidery'
      );
      if (method?.name) return method.name;
    }
  }
  const fallback = positions[0]?.decorationMethods;
  return fallback?.name || fallback?.description || '';
}
